/**
 * SSE Client
 *
 * Client for Server-Sent Events real-time updates.
 * Handles automatic reconnection and token refresh.
 */

import { getAccessToken, authApi } from './api';

export type EventType =
  | 'connected'
  | 'evaluation:updated'
  | 'evaluation:status_changed'
  | 'evaluation:score_updated'
  | 'evaluation:comment_added'
  | 'evaluation:agreement_added'
  | 'employee:updated'
  | 'notification'
  | 'ping';

export interface SSEEvent {
  type: EventType;
  payload: unknown;
  timestamp: string;
}

type EventHandler = (event: SSEEvent) => void;

class SSEClient {
  private eventSource: EventSource | null = null;
  private handlers: Map<EventType | '*', EventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isConnecting = false;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Connect to SSE endpoint
   * Automatically refreshes token before connecting if needed
   */
  async connect(): Promise<void> {
    if (this.eventSource || this.isConnecting) {
      return;
    }

    let token = getAccessToken();
    if (!token) {
      console.warn('No auth token available for SSE connection');
      return;
    }

    // Check if token might be expired and try to refresh
    // JWT tokens have 3 parts separated by dots
    try {
      const payload = JSON.parse(atob(token.split('.')[1]!));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      // If token expires in less than 60 seconds, refresh first
      if (expiresAt - now < 60000) {
        console.log('SSE: Token expiring soon, refreshing before connect');
        const refreshed = await authApi.refresh();
        if (refreshed) {
          token = getAccessToken();
          if (!token) {
            console.error('SSE: Token refresh succeeded but no token available');
            return;
          }
        } else {
          console.warn('SSE: Token refresh failed, attempting connection with current token');
        }
      }
    } catch {
      // If we can't parse the token, just try to connect
    }

    this.isConnecting = true;
    const baseUrl = import.meta.env.VITE_API_BASE || '/api/v1';

    // EventSource doesn't support custom headers, so we pass token as query param
    // This is a common workaround for SSE authentication
    this.eventSource = new EventSource(`${baseUrl}/events?token=${encodeURIComponent(token ?? '')}`);

    this.eventSource.onopen = () => {
      console.log('SSE connection established');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.isConnecting = false;
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as SSEEvent;
        this.emit(data);
      } catch (error) {
        console.error('Failed to parse SSE message:', error);
      }
    };

    this.eventSource.onerror = (_error) => {
      console.error('SSE connection error');
      this.isConnecting = false;
      this.eventSource?.close();
      this.eventSource = null;

      // Clear any existing reconnect timeout
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }

      // Attempt reconnection with exponential backoff
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
        console.log(`SSE: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.reconnectTimeoutId = setTimeout(() => {
          this.reconnectTimeoutId = null;
          this.connect();
        }, delay);
      } else {
        console.error('SSE: Max reconnection attempts reached');
        // Reset after a longer delay to allow manual retry
        this.reconnectTimeoutId = setTimeout(() => {
          this.reconnectTimeoutId = null;
          this.reconnectAttempts = 0;
        }, 60000);
      }
    };
  }

  /**
   * Disconnect from SSE
   */
  disconnect(): void {
    // Clear any pending reconnect
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.reconnectAttempts = 0;
    this.isConnecting = false;
  }

  /**
   * Subscribe to specific event type
   */
  on(eventType: EventType | '*', handler: EventHandler): () => void {
    const handlers = this.handlers.get(eventType) || [];
    handlers.push(handler);
    this.handlers.set(eventType, handlers);

    // Return unsubscribe function
    return () => {
      const currentHandlers = this.handlers.get(eventType) || [];
      const filtered = currentHandlers.filter((h) => h !== handler);
      this.handlers.set(eventType, filtered);
    };
  }

  /**
   * Emit event to handlers
   */
  private emit(event: SSEEvent): void {
    // Call specific handlers
    const handlers = this.handlers.get(event.type) || [];
    handlers.forEach((handler) => handler(event));

    // Call wildcard handlers
    const wildcardHandlers = this.handlers.get('*') || [];
    wildcardHandlers.forEach((handler) => handler(event));
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }
}

// Singleton instance
export const sseClient = new SSEClient();
